/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/Annotations/GitHubAnnotation" />

import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { useService } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Icon } from "VSSUI/Icon";
import { Spinner } from "VSSUI/Spinner";
import { Tooltip } from "VSSUI/TooltipEx";
import { css } from "VSSUI/Util";
import { IGitHubAnnotationInfo } from "Wit/BoardsHub/Common/Interfaces";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { ExternalArtifactType, ExternalLinkedArtifact } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { IAnnotationService } from "../../IAnnotationService";
import { AnnotationComponent } from "./Common";

/**
 * Implements the annotation for related GitHub items (issues, commits, prs)
 */
export const GitHubAnnotation: AnnotationComponent = ({ annotations, expanded, item, name, onClick }) => {
    const info = annotations?.gitHub;
    if (!info || info.links.length === 0) {
        return null;
    }
    return (
        <>
            <Button
                ariaLabel={format(Resources.GitHubLinks, info.links.length)}
                ariaExpanded={expanded}
                className={css("annotation", expanded && "active")}
                iconProps={{ className: "bowtie-brand-github bowtie-icon" }}
                onClick={() => onClick(name)}
                subtle
                tooltipProps={{
                    addAriaDescribedBy: true,
                    renderContent: () => (
                        <div>
                            <div>{format(Resources.GitHubPullRequest, info.pullRequests)}</div>
                            <div>{format(Resources.GitHubCommit, info.commits)}</div>
                            <div>{format(Resources.GitHubIssue, info.issues)}</div>
                        </div>
                    )
                }}
            >
                {info.links.length}
            </Button>
            {expanded && <GitHubAnnotationDetails info={info} itemId={item.value.id} />}
        </>
    );
};

const dateFormatter = new Intl.DateTimeFormat(undefined, { year: "numeric", month: "numeric", day: "numeric" });
function GitHubAnnotationDetails({ info, itemId }: { info: IGitHubAnnotationInfo; itemId: number }) {
    const annotationService = useService<IAnnotationService>("IAnnotationService");
    const [links, setLinks] = React.useState<ExternalLinkedArtifact[]>();
    React.useEffect(() => {
        annotationService.getGitHubAnnotationDetails(itemId, info.links).then(links => setLinks(links));
    }, [itemId]);

    return (
        <div className="annotation-expanded github-annotation-details">
            {!links ? (
                <Spinner />
            ) : (
                <ul>
                    {links?.map((link, i) => {
                        let iconName: string;
                        if (link.itemType === ExternalArtifactType.Commit) {
                            iconName = "BranchCommit";
                        } else if (link.itemType === ExternalArtifactType.PullRequest) {
                            iconName = "BranchPullRequest";
                        } else if (link.itemType === ExternalArtifactType.Issue) {
                            iconName = "WorkItem";
                        } else {
                            return null;
                        }
                        return (
                            <li key={i} className="flex-row">
                                <Icon className="margin-top-4 margin-right-4" iconName={iconName} />
                                <div className="flex-column">
                                    <Tooltip overflowOnly>
                                        <a href={link.url} target="_blank" rel="noopener">
                                            {link.title}
                                        </a>
                                    </Tooltip>
                                    <div className="secondary-text">
                                        {link.state
                                            ? format(Resources.UpdatedWithState, dateFormatter.format(link.date), link.state)
                                            : format(Resources.Updated, dateFormatter.format(link.date))}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
